/**
 * Product-card price and sale-badge synchronization.
 *
 * Consolidated here so collection and homepage grids register one set of
 * delegated listeners instead of repeating the same inline script per card.
 */
  (function() {
    // Format price with superscript cents (matches Liquid formatting)
    function getActiveCurrency() {
      if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
      return 'USD';
    }

    function formatPrice(priceInCents) {
      if (!priceInCents) return '';
      
      const price = parseInt(priceInCents, 10) / 100;
      const currency = getActiveCurrency();
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);
      
      const testPrice = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(0);
      const currencySymbol = testPrice.replace(/[\d.,\s]/g, '').trim() || testPrice.replace('0.00', '');
      const amount = formatted.replace(currencySymbol, '').trim();
      
      if (!amount || amount === '') {
        return `<span class="currency-symbol">${currencySymbol}</span>${formatted.replace(currencySymbol, '').trim()}`;
      }
      
      const normalizedAmount = amount.replace(/,/g, '');
      const [dollars, cents] = normalizedAmount.split('.');
      const centsPart = cents || '00';
      
      if (centsPart === '00') {
        return `<span class="currency-symbol">${currencySymbol}</span>${dollars}`;
      }
      return `<span class="currency-symbol">${currencySymbol}</span>${dollars}<span class="cents">.${centsPart}</span>`;
    }

    function updateSaleBadge(productCard, comparePriceCents, priceCents) {
      const imageContainer = productCard.querySelector('.product-card__image');
      if (!imageContainer) return;

      const compareCents = parseInt(comparePriceCents || '0', 10);
      const priceVal = parseInt(priceCents || '0', 10);
      const isOnSale = compareCents > priceVal && priceVal > 0;
      let badge = imageContainer.querySelector('.product-card.sale-badge');

      if (isOnSale) {
        const salePercentage = Math.round(((compareCents - priceVal) * 100) / compareCents);
        const badgeText = salePercentage > 0 ? `${salePercentage}% off` : 'Sale';
        if (badge) {
          badge.textContent = badgeText;
        } else {
          badge = document.createElement('div');
          badge.className = 'product-card sale-badge';
          badge.textContent = badgeText;
          imageContainer.appendChild(badge);
        }
      } else if (badge) {
        badge.remove();
      }
    }
    
    // Handle price updates and legend updates when color swatch is clicked
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('.color-swiper-container .color-swatch, .sunice-variant-picker .color-swatch');
      if (!swatch) return;
      
      const productCard = swatch.closest('.sunice-product-card');
      if (!productCard || productCard.classList.contains('sunice-variant-product-card') || productCard.hasAttribute('data-variant-id')) return;
      
      // Update product card link URL with variant ID from swatch
      const variantId = swatch.getAttribute('data-variant-id');
      if (variantId) {
        const productCardLink = productCard.querySelector('a[href*="/products/"]');
        const productTitleLink = productCard.querySelector('.product-card__title a, .product-title a');
        const cardGalleryLink = productCard.querySelector('.product-card__gallery-link');
        
        // Get base product URL (remove existing variant param)
        let baseUrl = '';
        if (productCardLink) {
          baseUrl = productCardLink.href.split('?')[0];
        } else {
          // Fallback: try to get from product card data attribute or picker
          const picker = productCard.querySelector('.sunice-variant-picker');
          if (picker) {
            baseUrl = picker.getAttribute('data-product-url') || '';
          }
        }
        
        if (baseUrl) {
          const url = new URL(baseUrl, window.location.origin);
          url.searchParams.set('variant', variantId);
          const newUrl = url.toString();
          
          // Update all product card links
          if (productCardLink) {
            productCardLink.href = newUrl;
          }
          if (productTitleLink) {
            productTitleLink.href = newUrl;
          }
          if (cardGalleryLink) {
            cardGalleryLink.href = newUrl;
          }
        }
      }
      
      // Update Core legend with selected color name
      const productId = productCard.getAttribute('data-product-id');
      const coreLegend = productCard.querySelector(`.core-legend[data-product-id="${productId}"]`);
      if (coreLegend) {
        // For variant picker swatches, get the color from the radio button value
        let colorName = swatch.getAttribute('data-color') || swatch.getAttribute('title') || '';
        
        // If it's a variant picker swatch and we don't have color yet, try to get from radio button
        if (!colorName && swatch.closest('.sunice-variant-picker')) {
          const radio = swatch.querySelector('input[type="radio"]');
          if (radio) {
            colorName = radio.value || '';
          }
        }
        
        if (colorName) {
          // Only update the span, not the entire legend
          let valueSpan = coreLegend.querySelector('.variant-option__selected-value');
          if (!valueSpan) {
            valueSpan = document.createElement('span');
            valueSpan.className = 'variant-option__selected-value';
            // Make sure legend only contains "Core:" before adding span
            coreLegend.textContent = 'Core:';
            coreLegend.appendChild(valueSpan);
          }
          valueSpan.textContent = colorName;
        }
      }
      
      const priceContainer = productCard.querySelector('.product-card__price-container');
      if (!priceContainer) return;
      
      // Get price data from swatch (prices are in cents)
      let variantPriceCents = swatch.getAttribute('data-variant-price');
      let variantComparePriceCents = swatch.getAttribute('data-variant-compare-price');
      
      // Fallback: If price attributes are missing, try to get from variant data
      if (!variantPriceCents || variantPriceCents === '') {
        const variantId = swatch.getAttribute('data-variant-id');
        if (variantId) {
          // Try to get variant data from JSON script tag
          const variantsDataScript = productCard.querySelector('.product-variants-data');
          if (variantsDataScript) {
            try {
              const productData = JSON.parse(variantsDataScript.textContent || '{}');
              const variantsList = productData?.variants || [];
              const matchingVariant = variantsList.find(v => String(v.id) === String(variantId));
              if (matchingVariant && matchingVariant.price) {
                variantPriceCents = String(matchingVariant.price);
                if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
                  variantComparePriceCents = String(matchingVariant.compare_at_price);
                }
              }
            } catch (error) {
              console.error('Error parsing variant data:', error);
            }
          }
        }
        // If we still don't have a price, return early
        if (!variantPriceCents || variantPriceCents === '') return;
      }
      
      // Format prices
      const formattedPrice = formatPrice(variantPriceCents);
      const formattedComparePrice = variantComparePriceCents ? formatPrice(variantComparePriceCents) : '';
      const isOnSale = variantComparePriceCents && parseInt(variantComparePriceCents) > parseInt(variantPriceCents);
      
      // Update price display
      const priceElement = priceContainer.querySelector('.product-card__price');
      const comparePriceElement = priceContainer.querySelector('.product-card__compare-price');
      
      if (priceElement && formattedPrice) {
        priceElement.innerHTML = formattedPrice;
        
        // Update sale class
        if (isOnSale) {
          priceElement.classList.add('product-card__price--sale');
          priceElement.classList.remove('product-card__price--regular');
        } else {
          priceElement.classList.remove('product-card__price--sale');
          priceElement.classList.add('product-card__price--regular');
        }
      }
      
      // Update compare price
      if (isOnSale && formattedComparePrice) {
        if (comparePriceElement) {
          comparePriceElement.innerHTML = formattedComparePrice;
        } else {
          // Create compare price element if it doesn't exist
          const newComparePrice = document.createElement('span');
          newComparePrice.className = 'product-card__compare-price';
          newComparePrice.innerHTML = formattedComparePrice;
          priceContainer.appendChild(newComparePrice);
        }
      } else {
        // Remove compare price if not on sale
        if (comparePriceElement) {
          comparePriceElement.remove();
        }
      }

      updateSaleBadge(productCard, variantComparePriceCents, variantPriceCents);
    });
    
    // Handle Core legend updates when color radio button is selected in variant picker form
    document.addEventListener('change', function(e) {
      const radio = e.target;
      if (!radio || radio.type !== 'radio') return;
      
      // Check if this is a Color/Colour option radio button
      const label = radio.closest('label');
      if (!label || !label.classList.contains('color-swatch')) return;
      
      const productCard = radio.closest('.sunice-product-card');
      if (!productCard) return;
      
      const productId = productCard.getAttribute('data-product-id');
      const coreLegend = productCard.querySelector(`.core-legend[data-product-id="${productId}"]`);
      if (coreLegend) {
        // Get color name from the radio button value
        const colorName = radio.value || '';
        if (colorName) {
          // Only update the span, not the entire legend
          let valueSpan = coreLegend.querySelector('.variant-option__selected-value');
          if (!valueSpan) {
            valueSpan = document.createElement('span');
            valueSpan.className = 'variant-option__selected-value';
            // Make sure legend only contains "Core:" before adding span
            coreLegend.textContent = 'Core:';
            coreLegend.appendChild(valueSpan);
          }
          valueSpan.textContent = colorName;
        }
      }
    });
    
    // Initialize Core legend with first selected/available color when form opens
    function initializeCoreLegend(productCard) {
      if (!productCard) return;
      
      const productId = productCard.getAttribute('data-product-id');
      const coreLegend = productCard.querySelector(`.core-legend[data-product-id="${productId}"]`);
      if (!coreLegend) return;
      
      // Only update the span, not the entire legend
      let valueSpan = coreLegend.querySelector('.variant-option__selected-value');
      if (!valueSpan) {
        valueSpan = document.createElement('span');
        valueSpan.className = 'variant-option__selected-value';
        // Make sure legend only contains "Core:" before adding span
        coreLegend.textContent = 'Core:';
        coreLegend.appendChild(valueSpan);
      }
      
      // Try to find the first checked/selected color radio button
      const checkedRadio = productCard.querySelector('.sunice-variant-picker .color-swatch input[type="radio"]:checked');
      if (checkedRadio && checkedRadio.value) {
        valueSpan.textContent = checkedRadio.value;
        return;
      }
      
      // Fallback: Find first available color swatch
      const firstAvailableSwatch = productCard.querySelector('.color-swiper-container .color-swatch[data-active="true"], .sunice-variant-picker .color-swatch[data-option-available="true"]');
      if (firstAvailableSwatch) {
        const colorName = firstAvailableSwatch.getAttribute('data-color') || firstAvailableSwatch.getAttribute('title') || '';
        if (colorName) {
          valueSpan.textContent = colorName;
        }
      }
    }
    
    // Initialize on page load for all product cards
    document.addEventListener('DOMContentLoaded', function() {
      document.querySelectorAll('.sunice-product-card').forEach(function(card) {
        initializeCoreLegend(card);
      });
    });
    
    // Also initialize when form is opened (in case cards are loaded dynamically)
    document.addEventListener('click', function(e) {
      if (e.target && e.target.classList.contains('select-size-button')) {
        const productCard = e.target.closest('.sunice-product-card');
        setTimeout(function() {
          initializeCoreLegend(productCard);
        }, 100);
      }
    });
  })();

  (function() {
    // Format price with superscript cents (matches Liquid formatting)
    function getActiveCurrency() {
      if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
      return 'USD';
    }

    function formatPrice(priceInCents) {
      if (!priceInCents) return '';
      
      const price = parseInt(priceInCents, 10) / 100;
      const currency = getActiveCurrency();
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);
      
      const testPrice = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(0);
      const currencySymbol = testPrice.replace(/[\d.,\s]/g, '').trim() || testPrice.replace('0.00', '');
      const amount = formatted.replace(currencySymbol, '').trim();
      
      if (!amount || amount === '') {
        return `<span class="currency-symbol">${currencySymbol}</span>${formatted.replace(currencySymbol, '').trim()}`;
      }
      
      const normalizedAmount = amount.replace(/,/g, '');
      const [dollars, cents] = normalizedAmount.split('.');
      const centsPart = cents || '00';
      
      if (centsPart === '00') {
        return `<span class="currency-symbol">${currencySymbol}</span>${dollars}`;
      }
      return `<span class="currency-symbol">${currencySymbol}</span>${dollars}<span class="cents">.${centsPart}</span>`;
    }

    function updateSaleBadge(productCard, comparePriceCents, priceCents) {
      const imageContainer = productCard.querySelector('.product-card__image');
      if (!imageContainer) return;

      const compareCents = parseInt(comparePriceCents || '0', 10);
      const priceVal = parseInt(priceCents || '0', 10);
      const isOnSale = compareCents > priceVal && priceVal > 0;
      let badge = imageContainer.querySelector('.product-card.sale-badge');

      if (isOnSale) {
        const salePercentage = Math.round(((compareCents - priceVal) * 100) / compareCents);
        const badgeText = salePercentage > 0 ? `${salePercentage}% off` : 'Sale';
        if (badge) {
          badge.textContent = badgeText;
        } else {
          badge = document.createElement('div');
          badge.className = 'product-card sale-badge';
          badge.textContent = badgeText;
          imageContainer.appendChild(badge);
        }
      } else if (badge) {
        badge.remove();
      }
    }
    
    // Handle price updates when color swatch is clicked in the slider
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('.sunice-variant-product-card .color-swiper-container .color-swatch, .sunice-product-card[data-variant-id] .color-swiper-container .color-swatch');
      if (!swatch) return;
      
      const productCard = swatch.closest('.sunice-variant-product-card, .sunice-product-card[data-variant-id]');
      if (!productCard) return;
      
      // Update product card link URL with variant ID from swatch
      const variantId = swatch.getAttribute('data-variant-id');
      if (variantId) {
        const productCardLink = productCard.querySelector('a[href*="/products/"]');
        const productTitleLink = productCard.querySelector('.product-card__title a, .product-title a');
        const cardGalleryLink = productCard.querySelector('.product-card__gallery-link');
        
        // Get base product URL (remove existing variant param)
        let baseUrl = '';
        if (productCardLink) {
          baseUrl = productCardLink.href.split('?')[0];
        } else {
          // Fallback: try to get from product card data attribute or picker
          const picker = productCard.querySelector('.sunice-variant-picker');
          if (picker) {
            baseUrl = picker.getAttribute('data-product-url') || '';
          }
        }
        
        if (baseUrl) {
          const url = new URL(baseUrl, window.location.origin);
          url.searchParams.set('variant', variantId);
          const newUrl = url.toString();
          
          // Update all product card links
          if (productCardLink) {
            productCardLink.href = newUrl;
          }
          if (productTitleLink) {
            productTitleLink.href = newUrl;
          }
          if (cardGalleryLink) {
            cardGalleryLink.href = newUrl;
          }
        }
      }
      
      const priceContainer = productCard.querySelector('.product-card__price-container');
      if (!priceContainer) return;
      
      let variantPriceCents = swatch.getAttribute('data-variant-price');
      let variantComparePriceCents = swatch.getAttribute('data-variant-compare-price');
      
      // Fallback: If price attributes are missing, try to get from variant data
      if (!variantPriceCents || variantPriceCents === '') {
        const variantId = swatch.getAttribute('data-variant-id');
        if (variantId) {
          // Try to get variant data from JSON script tag
          const variantsDataScript = productCard.querySelector('.product-variants-data');
          if (variantsDataScript) {
            try {
              const productData = JSON.parse(variantsDataScript.textContent || '{}');
              const variantsList = productData?.variants || [];
              const matchingVariant = variantsList.find(v => String(v.id) === String(variantId));
              if (matchingVariant && matchingVariant.price) {
                variantPriceCents = String(matchingVariant.price);
                if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
                  variantComparePriceCents = String(matchingVariant.compare_at_price);
                }
              }
            } catch (error) {
              console.error('Error parsing variant data:', error);
            }
          }
        }
        // If we still don't have a price, return early
        if (!variantPriceCents || variantPriceCents === '') return;
      }
      
      const formattedPrice = formatPrice(variantPriceCents);
      const formattedComparePrice = variantComparePriceCents ? formatPrice(variantComparePriceCents) : '';
      const isOnSale = variantComparePriceCents && parseInt(variantComparePriceCents) > parseInt(variantPriceCents);
      
      const priceElement = priceContainer.querySelector('.product-card__price');
      const comparePriceElement = priceContainer.querySelector('.product-card__compare-price');
      
      if (priceElement && formattedPrice) {
        priceElement.innerHTML = formattedPrice;
        
        if (isOnSale) {
          priceElement.classList.add('product-card__price--sale');
          priceElement.classList.remove('product-card__price--regular');
        } else {
          priceElement.classList.remove('product-card__price--sale');
          priceElement.classList.add('product-card__price--regular');
        }
      }
      
      if (isOnSale && formattedComparePrice) {
        if (comparePriceElement) {
          comparePriceElement.innerHTML = formattedComparePrice;
        } else {
          const newComparePrice = document.createElement('span');
          newComparePrice.className = 'product-card__compare-price';
          newComparePrice.innerHTML = formattedComparePrice;
          priceContainer.appendChild(newComparePrice);
        }
      } else {
        if (comparePriceElement) {
          comparePriceElement.remove();
        }
      }

      updateSaleBadge(productCard, variantComparePriceCents, variantPriceCents);
    });
  })();
